import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, TrendingDown, DollarSign, Calendar, CreditCard, Building2, Wallet, BarChart3, PieChart, TrendingUp } from "lucide-react";
import { format, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { LineChart, Line, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { useTenant } from "@/contexts/TenantContext";

type ExpenseCategory = {
  id: string;
  nome: string;
  descricao: string | null;
  cor_hex: string | null;
  icone: string | null;
  status: string | null;
  created_at: string;
};

type Transaction = {
  id: string;
  tipo: string;
  valor_total: number;
  valor_liquido: number;
  desconto: number;
  status: string;
  created_at: string;
  descricao: string | null;
  supplier_id: string | null;
  bank_account_id: string | null;
  payment_method_id: string | null;
  pagamento_json?: any;
};

type CategoryStats = {
  totalExpenses: number;
  totalSpent: number;
  averageExpense: number;
  maxExpense: number;
  minExpense: number;
  firstExpense: string | null;
  lastExpense: string | null;
  totalPaid: number;
  totalPending: number;
  totalCanceled: number;
  monthlySpending: { month: string; value: number; count: number }[];
  paymentMethods: { method: string; value: number; count: number }[];
  paymentStatus: { status: string; value: number; count: number }[];
  suppliers: { supplier: string; value: number; count: number }[];
  bankAccounts: { account: string; value: number; count: number }[];
  weeklyDistribution: { day: string; value: number; count: number }[];
  growthTrend: number;
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", { 
    style: "currency", 
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

export default function ExpenseCategoryDetails(props?: { id?: string; embedded?: boolean; periodRange?: [Date, Date]; setLoading?: (loading: boolean) => void }) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = props?.id ?? routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useTenant();
  const search = new URLSearchParams(location.search);
  const isEmbedded = (typeof props?.embedded !== 'undefined') ? !!props?.embedded : (search.get("embedded") === "1");
  const fromStr = search.get("from");
  const toStr = search.get("to");
  const [cadFrom, setCadFrom] = useState<Date>(props?.periodRange ? startOfDay(props.periodRange[0]) : (fromStr ? startOfDay(new Date(fromStr)) : startOfDay(new Date(0))));
  const [cadTo, setCadTo] = useState<Date>(props?.periodRange ? endOfDay(props.periodRange[1]) : (toStr ? endOfDay(new Date(toStr)) : endOfDay(new Date())));
  useEffect(() => {
    if (isEmbedded && !props?.periodRange) {
      const handler = (e: MessageEvent) => {
        const data = (e.data || {}) as any;
        if (data && data.type === 'rx-period-change') {
          setCadFrom(startOfDay(new Date(data.from)));
          setCadTo(endOfDay(new Date(data.to)));
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
  }, [isEmbedded, props?.periodRange]);

  const { data: category, isLoading: categoryLoading } = useQuery({
    queryKey: ["expense-category", id, tenantId],
    queryFn: async () => {
      if (!tenantId || !id) return null;
      try {
        const { data, error } = await (supabase as any)
          .from("expense_categories")
          .select("*")
          .eq("tenant_id", tenantId)
          .eq("id", id)
          .maybeSingle();
        // Se não encontrar na tabela, retorna null (a categoria pode existir apenas nas transações)
        if (error) {
          // PGRST116 = nenhum resultado encontrado (normal quando categoria não está cadastrada)
          if (error.code === 'PGRST116') {
            return null;
          }
          console.error("Erro ao buscar categoria:", error);
          return null;
        }
        return (data || null) as any;
      } catch (err) {
        console.error("Erro ao buscar categoria:", err);
        return null;
      }
    },
    enabled: !!id && !!tenantId,
    retry: false, // Não tentar novamente se falhar
  });

  const categoryName = category?.nome || "";

  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ["category-transactions", categoryName, tenantId, cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      if (!tenantId || !categoryName) return [];
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("tenant_id", tenantId)
        .eq("tipo", "saida")
        .eq("categoria", categoryName)
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString())
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!categoryName && !!tenantId,
  });

  useEffect(() => {
    const loading = categoryLoading || transactionsLoading;
    if (isEmbedded) {
      window.parent?.postMessage({ type: 'rx-loading', loading }, '*');
    }
    if (props?.setLoading) props.setLoading(loading);
  }, [categoryLoading, transactionsLoading, isEmbedded, props?.setLoading]);

  // Buscar nomes de fornecedores
  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("suppliers")
        .select("id, nome")
        .eq("tenant_id", tenantId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  // Buscar nomes de contas bancárias
  const { data: bankAccounts = [] } = useQuery({
    queryKey: ["bank-accounts", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id, nome")
        .eq("tenant_id", tenantId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  // Buscar nomes de métodos de pagamento
  const { data: paymentMethods = [] } = useQuery({
    queryKey: ["payment-methods", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("payment_methods")
        .select("id, nome");
      // Se der erro, retorna array vazio (pode não ter tenant_id na tabela)
      if (error) {
        console.error("Erro ao buscar métodos de pagamento:", error);
        return [];
      }
      return data || [];
    },
    enabled: !!tenantId,
  });

  const { data: stats } = useQuery({
    queryKey: ["category-stats", categoryName, transactions],
    queryFn: async () => {
      if (!transactions || transactions.length === 0) {
        return {
          totalExpenses: 0,
          totalSpent: 0,
          averageExpense: 0,
          maxExpense: 0,
          minExpense: 0,
          firstExpense: null,
          lastExpense: null,
          totalPaid: 0,
          totalPending: 0,
          totalCanceled: 0,
          monthlySpending: [],
          paymentMethods: [],
          paymentStatus: [],
          suppliers: [],
          bankAccounts: [],
          weeklyDistribution: [],
          growthTrend: 0,
        } as CategoryStats;
      }

      const expenses = transactions;
      const totalSpent = expenses.reduce((sum, t) => sum + Number(t.valor_liquido), 0);
      const totalExpenses = expenses.length;
      const averageExpense = totalExpenses > 0 ? totalSpent / totalExpenses : 0;
      const values = expenses.map(t => Number(t.valor_liquido));
      const maxExpense = Math.max(...values);
      const minExpense = Math.min(...values);
      const sortedByDate = [...expenses].sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      const firstExpense = sortedByDate.length > 0 ? sortedByDate[0].created_at : null;
      const lastExpense = expenses.length > 0 ? expenses[0].created_at : null;

      // Status
      const totalPaid = expenses.filter(t => t.status === "paga").reduce((sum, t) => sum + Number(t.valor_liquido), 0);
      const totalPending = expenses.filter(t => t.status === "pendente").reduce((sum, t) => sum + Number(t.valor_liquido), 0);
      const totalCanceled = expenses.filter(t => t.status === "cancelada").reduce((sum, t) => sum + Number(t.valor_liquido), 0);

      // Mensal
      const monthlyMap = new Map<string, { value: number; count: number }>();
      expenses.forEach(expense => {
        const month = format(new Date(expense.created_at), "MMM/yyyy", { locale: ptBR });
        const current = monthlyMap.get(month) || { value: 0, count: 0 };
        monthlyMap.set(month, {
          value: current.value + Number(expense.valor_liquido),
          count: current.count + 1
        });
      });
      const monthlySpending = Array.from(monthlyMap.entries())
        .map(([month, data]) => ({ month, ...data }))
        .sort((a, b) => {
          const dateA = new Date(a.month.split("/")[1] + "-" + a.month.split("/")[0]);
          const dateB = new Date(b.month.split("/")[1] + "-" + b.month.split("/")[0]);
          return dateA.getTime() - dateB.getTime();
        });

      // Métodos de pagamento
      const paymentMethodMap = new Map<string, { value: number; count: number }>();
      expenses.forEach(expense => {
        const methodId = expense.payment_method_id;
        if (methodId) {
          const method = paymentMethods.find(pm => pm.id === methodId);
          const methodName = method?.nome || methodId;
          const current = paymentMethodMap.get(methodName) || { value: 0, count: 0 };
          paymentMethodMap.set(methodName, {
            value: current.value + Number(expense.valor_liquido),
            count: current.count + 1
          });
        }
      });
      const paymentMethodsData = Array.from(paymentMethodMap.entries())
        .map(([method, data]) => ({ method, ...data }))
        .sort((a, b) => b.value - a.value);

      // Status de pagamento
      const statusMap = new Map<string, { value: number; count: number }>();
      expenses.forEach(expense => {
        const status = expense.status || "pendente";
        const current = statusMap.get(status) || { value: 0, count: 0 };
        statusMap.set(status, {
          value: current.value + Number(expense.valor_liquido),
          count: current.count + 1
        });
      });
      const paymentStatus = Array.from(statusMap.entries())
        .map(([status, data]) => ({ status, ...data }));

      // Fornecedores
      const supplierMap = new Map<string, { value: number; count: number }>();
      expenses.forEach(expense => {
        if (expense.supplier_id) {
          const supplier = suppliers.find(s => s.id === expense.supplier_id);
          const supplierName = supplier?.nome || expense.supplier_id;
          const current = supplierMap.get(supplierName) || { value: 0, count: 0 };
          supplierMap.set(supplierName, {
            value: current.value + Number(expense.valor_liquido),
            count: current.count + 1
          });
        }
      });
      const suppliersData = Array.from(supplierMap.entries())
        .map(([supplier, data]) => ({ supplier, ...data }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      // Contas bancárias
      const bankAccountMap = new Map<string, { value: number; count: number }>();
      expenses.forEach(expense => {
        if (expense.bank_account_id) {
          const account = bankAccounts.find(ba => ba.id === expense.bank_account_id);
          const accountName = account?.nome || expense.bank_account_id;
          const current = bankAccountMap.get(accountName) || { value: 0, count: 0 };
          bankAccountMap.set(accountName, {
            value: current.value + Number(expense.valor_liquido),
            count: current.count + 1
          });
        }
      });
      const bankAccountsData = Array.from(bankAccountMap.entries())
        .map(([account, data]) => ({ account, ...data }))
        .sort((a, b) => b.value - a.value);

      // Distribuição semanal
      const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
      const weeklyMap = new Map<string, { value: number; count: number }>();
      weekDays.forEach(day => weeklyMap.set(day, { value: 0, count: 0 }));
      expenses.forEach(expense => {
        const day = weekDays[new Date(expense.created_at).getDay()];
        const current = weeklyMap.get(day) || { value: 0, count: 0 };
        weeklyMap.set(day, {
          value: current.value + Number(expense.valor_liquido),
          count: current.count + 1
        });
      });
      const weeklyDistribution = Array.from(weeklyMap.entries())
        .map(([day, data]) => ({ day, ...data }));

      // Tendência de crescimento (comparar últimos 3 meses com os 3 anteriores)
      const now = new Date();
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
      const recent = expenses.filter(t => new Date(t.created_at) >= threeMonthsAgo)
        .reduce((sum, t) => sum + Number(t.valor_liquido), 0);
      const previous = expenses.filter(t => {
        const date = new Date(t.created_at);
        return date >= sixMonthsAgo && date < threeMonthsAgo;
      }).reduce((sum, t) => sum + Number(t.valor_liquido), 0);
      const growthTrend = previous > 0 ? ((recent - previous) / previous) * 100 : 0;

      return {
        totalExpenses,
        totalSpent,
        averageExpense,
        maxExpense,
        minExpense,
        firstExpense,
        lastExpense,
        totalPaid,
        totalPending,
        totalCanceled,
        monthlySpending,
        paymentMethods: paymentMethodsData,
        paymentStatus,
        suppliers: suppliersData,
        bankAccounts: bankAccountsData,
        weeklyDistribution,
        growthTrend,
      } as CategoryStats;
    },
    enabled: !!transactions,
  });

  if (categoryLoading || transactionsLoading) {
    return (
      <div className={`${isEmbedded ? 'px-0 py-2' : 'container mx-auto py-6'}`}>
        <div className="text-center">Carregando...</div>
      </div>
    );
  }

  // Verificar se há transações para esta categoria (mesmo que a categoria não esteja cadastrada)
  if (!transactions || transactions.length === 0) {
    return (
      <div className={`${isEmbedded ? 'px-0 py-2' : 'container mx-auto py-6'}`}>
        <div className="text-center">
          <p className="text-muted-foreground">Nenhuma transação encontrada para a categoria "{categoryName}"</p>
          {!isEmbedded && (
          <Button onClick={() => navigate("/caixa")} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${isEmbedded ? 'px-0 py-2 space-y-4' : 'container mx-auto py-6 space-y-6'}`}>
      {!isEmbedded && (
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/caixa")}> 
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Categoria de Despesa: {categoryName || "Carregando..."}</h1>
            {category?.descricao && (
              <p className="text-muted-foreground mt-1">{category.descricao}</p>
            )}
          </div>
        </div>
      )}

      {stats && (
        <>
          {/* Cards de Estatísticas */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Despesas</CardTitle>
                <TrendingDown className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalExpenses}</div>
                <p className="text-xs text-muted-foreground">Transações registradas</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Valor Total</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(stats.totalSpent)}</div>
                <p className="text-xs text-muted-foreground">
                  Média: {formatCurrency(stats.averageExpense)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Maior Despesa</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(stats.maxExpense)}</div>
                <p className="text-xs text-muted-foreground">
                  Menor: {formatCurrency(stats.minExpense)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tendência</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${stats.growthTrend >= 0 ? 'text-destructive' : 'text-success'}`}>
                  {stats.growthTrend >= 0 ? '+' : ''}{stats.growthTrend.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">Últimos 3 meses</p>
              </CardContent>
            </Card>
          </div>

          {/* Status */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Pago</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-success">{formatCurrency(stats.totalPaid)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Pendente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-warning">{formatCurrency(stats.totalPending)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Cancelado</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-muted-foreground">{formatCurrency(stats.totalCanceled)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs com Gráficos */}
          <Tabs defaultValue="evolucao" className="space-y-4">
            <TabsList>
              <TabsTrigger value="evolucao">Evolução de Gastos</TabsTrigger>
              <TabsTrigger value="metodos">Métodos de Pagamento</TabsTrigger>
              <TabsTrigger value="status">Status</TabsTrigger>
              <TabsTrigger value="fornecedores">Fornecedores</TabsTrigger>
              <TabsTrigger value="contas">Contas Bancárias</TabsTrigger>
              <TabsTrigger value="semanal">Distribuição Semanal</TabsTrigger>
              <TabsTrigger value="transacoes">Transações</TabsTrigger>
            </TabsList>

            <TabsContent value="evolucao" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Evolução de Gastos por Mês</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.monthlySpending}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                      <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#8884d8" 
                        name="Valor Total"
                        strokeWidth={2}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="count" 
                        stroke="#82ca9d" 
                        name="Quantidade"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="metodos" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Distribuição por Método de Pagamento</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={stats.paymentMethods}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry: any) => `${entry.method}: ${formatCurrency(entry.value)}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {stats.paymentMethods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="status" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Distribuição por Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={stats.paymentStatus}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry: any) => `${entry.status}: ${formatCurrency(entry.value)}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {stats.paymentStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="fornecedores" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Top Fornecedores</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.suppliers}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="supplier" angle={-45} textAnchor="end" height={100} />
                      <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                      <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="value" fill="#8884d8" name="Valor Total" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contas" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Distribuição por Conta Bancária</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.bankAccounts}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="account" angle={-45} textAnchor="end" height={100} />
                      <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                      <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="value" fill="#82ca9d" name="Valor Total" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="semanal" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Distribuição por Dia da Semana</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.weeklyDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                      <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="value" fill="#ffc658" name="Valor Total" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="transacoes" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Lista de Transações</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Fornecedor</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions && transactions.length > 0 ? (
                          transactions.map((transaction) => {
                            const supplier = suppliers.find(s => s.id === transaction.supplier_id);
                            return (
                              <TableRow key={transaction.id}>
                                <TableCell>
                                  {format(new Date(transaction.created_at), "dd/MM/yyyy", { locale: ptBR })}
                                </TableCell>
                                <TableCell className="max-w-xs truncate">
                                  {transaction.descricao || "-"}
                                </TableCell>
                                <TableCell>
                                  {supplier?.nome || "-"}
                                </TableCell>
                                <TableCell>
                                  <Badge variant={transaction.status === "paga" ? "default" : "secondary"}>
                                    {transaction.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {formatCurrency(transaction.valor_liquido)}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-muted-foreground">
                              Nenhuma transação encontrada
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

